import { AxiosError } from "axios";
import { api } from "@/shared/api/client";
import type {
  BroadcastInput,
  Channel,
  MerchantOption,
  RecipientMode,
  SentNotification,
  SentStatus,
} from "./types";
import {
  mockCancelScheduled,
  mockListNotifications,
  mockSearchMerchants,
  mockSendBroadcast,
} from "./mock";

// ── Mock toggle ──────────────────────────────────────────────────────────────
// Same convention as the customers/settings/import features: LOCAL runs against an
// in-memory mock; STAGING/PROD hit the real backend (FCM already configured there).
// FLIP TO REAL: VITE_USE_MOCK=false — no other change needed.
const USE_MOCK =
  (import.meta.env.VITE_USE_MOCK ?? "true").toString().toLowerCase() !== "false";

// ── Capability flags ──────────────────────────────────────────────────────────
// Task 0082 (Phase 2) made SMS/BOTH, scheduling, and the SINGLE merchant picker REAL on
// the backend (per-user multilingual SMS via Sayqal; a scheduled_at sweep; GET
// /support/merchants/?search=). So those are now ON in every mode. The ONE thing still
// mock-only is the compose-time audience estimate — there is no segment-size endpoint, so
// real mode hides the estimate card and shows the true recipient_count in the history
// table after a send instead.
export const supportsSms = true;
export const supportsSchedule = true;
export const supportsSingle = true;
export const supportsEstimate = USE_MOCK;

// Small latency so the loading skeleton is exercised in mock mode.
const delay = (ms = 250) => new Promise<void>((r) => setTimeout(r, ms));

// frontend mode → backend enum
const AUDIENCE_TO_API: Record<RecipientMode, "ALL" | "SEGMENT" | "SINGLE"> = {
  all: "ALL",
  segment: "SEGMENT",
  single: "SINGLE",
};
const CHANNEL_TO_API: Record<Channel, "PUSH" | "SMS" | "BOTH"> = {
  push: "PUSH",
  sms: "SMS",
  both: "BOTH",
};
// segment-status option key → backend UserStatus value (segStatusAll → no filter).
const STATUS_TO_API: Record<string, string | undefined> = {
  segStatusAll: undefined,
  segStatusActive: "ACTIVE",
  segStatusInactive: "INACTIVE",
  segStatusNew: "NEW",
};

// Shape of the real feed rows (DRF SystemNotificationsSerializer). `title`/`text` are localized to
// the reader's language by the backend; the raw `*_uz`/`*_ru` fields (task 0081) let the dashboard
// pick the column independently of the API caller's language.
interface RawSystemNotification {
  id: number;
  title: string;
  text: string;
  title_uz: string | null;
  title_ru: string | null;
  text_uz: string | null;
  text_ru: string | null;
  audience: "ALL" | "SEGMENT" | "SINGLE";
  channel: "PUSH" | "SMS" | "BOTH";
  segment_status: string | null;
  segment_premium: boolean;
  status: "DRAFT" | "SENT" | "SCHEDULED" | "CANCELLED";
  recipient_count: number;
  scheduled_at: string | null;
  sent_at: string | null;
  created_time: string;
}

const STATUS_FROM_API: Record<RawSystemNotification["status"], SentStatus> = {
  DRAFT: "draft",
  SENT: "sent",
  SCHEDULED: "scheduled",
  CANCELLED: "cancelled",
};
// Uzbek labels for the recipients column (operator UI is Uzbek-first).
const SEGMENT_STATUS_LABEL_UZ: Record<string, string> = {
  ACTIVE: "Faol",
  INACTIVE: "Nofaol",
  NEW: "Yangi",
};

function rawRecipientsLabel(raw: RawSystemNotification): string {
  if (raw.audience === "SINGLE") return "Bitta mijoz";
  if (raw.audience === "SEGMENT") {
    const parts: string[] = [];
    const status = raw.segment_status
      ? SEGMENT_STATUS_LABEL_UZ[raw.segment_status]
      : undefined;
    if (status) parts.push(status);
    if (raw.segment_premium) parts.push("Premium");
    return parts.length ? parts.join(" · ") : "Segment";
  }
  return "Barcha mijozlar";
}

// Real feed → display row. recipient_count is the targeted send size (counted at
// send time); it is 0/unset for drafts, so show null there rather than a misleading 0.
function toSent(raw: RawSystemNotification): SentNotification {
  const status = STATUS_FROM_API[raw.status] ?? "sent";
  return {
    id: raw.id,
    // Fall back to the localized `title` for legacy rows that predate the bilingual columns.
    titleUz: raw.title_uz ?? raw.title,
    titleRu: raw.title_ru ?? raw.title,
    recipients: rawRecipientsLabel(raw),
    channel: (raw.channel?.toLowerCase() as Channel) ?? "push",
    delivered: status === "sent" ? raw.recipient_count : null,
    // SENT rows show when they went out; SCHEDULED/CANCELLED show the (intended) send time;
    // a DRAFT has neither, so it falls back to when it was created.
    date: raw.sent_at ?? raw.scheduled_at ?? raw.created_time,
    status,
  };
}

// ── Error mapping boundary ───────────────────────────────────────────────────
// The create serializer guards a send with a single { message } on a 400 (task 0081 requires both
// languages); the per-action permission returns 403 for non-operators. Normalise to a stable code at
// this one boundary so the screen never branches on raw server strings.
export type NotificationErrorCode = "contentRequired" | "forbidden" | "generic";

export class NotificationError extends Error {
  constructor(public code: NotificationErrorCode) {
    super(code);
    this.name = "NotificationError";
  }
}

function toNotificationError(e: unknown): NotificationError {
  // Mock path tags the thrown Error with `notifCode`; real path is an AxiosError.
  const tagged = (e as { notifCode?: NotificationErrorCode }).notifCode;
  if (tagged) return new NotificationError(tagged);
  if (e instanceof AxiosError) {
    if (e.response?.status === 403) return new NotificationError("forbidden");
    // A 400 is the both-languages-required guard (task 0081). The serializer also 400s a
    // past scheduled_at, but the compose form blocks that client-side (future-only picker),
    // so contentRequired is the message an operator can actually hit here.
    if (e.response?.status === 400) return new NotificationError("contentRequired");
  }
  return new NotificationError("generic");
}

// Build the real create body from the compose form. Targeting fields are only sent
// when the chosen audience uses them, matching the serializer's per-audience rules.
function toCreateBody(input: BroadcastInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    title_uz: input.titleUz,
    title_ru: input.titleRu,
    text_uz: input.textUz,
    text_ru: input.textRu,
    audience: AUDIENCE_TO_API[input.recipientMode],
    channel: CHANNEL_TO_API[input.channel],
    is_draft: !!input.isDraft,
  };
  if (input.recipientMode === "segment") {
    body.segment_status = STATUS_TO_API[input.customerStatus ?? "segStatusAll"] ?? null;
    body.segment_premium = !!input.segmentPremium;
  }
  if (input.recipientMode === "single" && input.targetUserId != null) {
    body.target_user = input.targetUserId;
  }
  // Scheduled send (task 0082): an ISO instant in the future → the backend persists it
  // SCHEDULED and a per-minute sweep delivers it once scheduled_at passes. Drafts never
  // schedule (is_draft wins), so only attach it for a real send.
  if (!input.isDraft && input.sendMode === "schedule" && input.scheduledAt) {
    body.scheduled_at = input.scheduledAt;
  }
  return body;
}

export async function listSentNotifications(): Promise<SentNotification[]> {
  if (USE_MOCK) {
    await delay();
    return mockListNotifications();
  }
  const res = await api.get<
    RawSystemNotification[] | { results: RawSystemNotification[] }
  >("/notifications/system-notifications/");
  // The feed endpoint is paginated (DRF default) but may be a bare array in some
  // configs — normalise both shapes, then map into the display model.
  const d = res.data;
  const rows = Array.isArray(d) ? d : d.results;
  return rows.map(toSent);
}

export async function sendBroadcast(input: BroadcastInput): Promise<SentNotification> {
  if (USE_MOCK) {
    await delay();
    try {
      return mockSendBroadcast(input);
    } catch (e) {
      throw toNotificationError(e);
    }
  }
  try {
    const res = await api.post<RawSystemNotification>(
      "/notifications/system-notifications/",
      toCreateBody({ ...input, isDraft: false }),
    );
    return toSent(res.data);
  } catch (e) {
    throw toNotificationError(e);
  }
}

// Save-as-draft. Mock mode is toast-only (no row); real mode persists a DRAFT row
// (operators see drafts in the feed; the merchant app never does — see the viewset).
export async function saveDraftBroadcast(input: BroadcastInput): Promise<void> {
  if (USE_MOCK) {
    await delay();
    return;
  }
  try {
    await api.post<RawSystemNotification>(
      "/notifications/system-notifications/",
      toCreateBody({ ...input, isDraft: true }),
    );
  } catch (e) {
    throw toNotificationError(e);
  }
}

// ── SINGLE merchant picker ─────────────────────────────────────────────────────
// GET /support/merchants/?search= surfaces every User that is a merchant of >=1 client
// (one row each, with a client_count). The operator types a name/phone, picks one, and
// its id becomes target_user on the broadcast. Paginated like the customers feed.
interface RawMerchant {
  id: number;
  first_name: string;
  last_name: string;
  phone_number: string;
  client_count: number;
}

function toMerchantOption(raw: RawMerchant): MerchantOption {
  return {
    id: raw.id,
    firstName: raw.first_name ?? "",
    lastName: raw.last_name ?? "",
    phoneNumber: raw.phone_number ?? "",
    clientCount: raw.client_count ?? 0,
  };
}

export async function searchMerchants(query: string): Promise<MerchantOption[]> {
  if (USE_MOCK) {
    await delay();
    return mockSearchMerchants(query);
  }
  const res = await api.get<RawMerchant[] | { results: RawMerchant[] }>(
    "/support/merchants/",
    { params: { search: query } },
  );
  const d = res.data;
  const rows = Array.isArray(d) ? d : d.results;
  return rows.map(toMerchantOption);
}

// ── Cancel a scheduled broadcast ────────────────────────────────────────────────
// POST /notifications/system-notifications/{id}/cancel/ — operator-only, SCHEDULED → CANCELLED.
// The sweep only delivers SCHEDULED rows, so a cancel before scheduled_at passes stops the send.
export async function cancelScheduled(id: number): Promise<void> {
  if (USE_MOCK) {
    await delay();
    mockCancelScheduled(id);
    return;
  }
  try {
    await api.post(`/notifications/system-notifications/${id}/cancel/`);
  } catch (e) {
    throw toNotificationError(e);
  }
}
